/**
 * `hiring-views` — the ONE contract surface behind the section-hosted Hiring
 * views. RO-3-00e (task #44472).
 *
 * ── Why a separate contract file, and why one op per VIEW ──
 *
 * `excellent/app/projects/[projectSlug]/[sectionSlug]/_hiring/views.tsx` is 1,366
 * LOC of server components that open the vault in their render path
 * (`docs/rollover/inventory/shared-render.md` §4 lists it as the largest single
 * blocked file in the cross-section shared render layer; `shared-render-ownership.md`
 * §6 assigns it here). It cannot be copied into `excellent/client/` at any effort
 * level — the data has to arrive over a contract instead.
 *
 * The precedent for the SHAPE is RO-3-02b's `workflowDetail.get`: "the server
 * render becomes ONE contracted read". Each view below composed 3–13 loader calls
 * inline; each op here is that composition, performed server-side in
 * `excellent/server/routes/hiring-views.ts`. One round trip per view, not
 * thirteen, and the composition stays where the vault is.
 *
 * ── Why not extend `hiring-jobs` / `hiring-candidates` / … instead ──
 *
 * Those seven contracts are the RESOURCE surface: CRUD over one entity, consumed
 * by the `@excellent/ui/blocks/hiring/*` islands (`JobForm`, `ApplicationActions`,
 * `PipelineSettings`, …) and by MCP. The views need view models — a job WITH its
 * applicant tally, a candidate WITH identifiers + attachments + notes + tags +
 * sync link — which is not any one resource. Widening `jobSchema` to carry a
 * `applicant_count` would push a page concern into the resource every other
 * consumer reads. So: reuse every existing SCHEMA (nothing below re-declares a
 * job, stage, application, scorecard, template or source shape — they are all
 * imported), add only the compositions.
 *
 * ── The path prefix is a SECURITY decision, not a naming one ──
 *
 * Every op below lives under `/hiring/api/views/…`, never `/hiring/api/jobs…`.
 * `PUBLIC_PREFIXES` in `packages/core/src/auth/route-gate.ts` waives
 * authentication for anything matching `path.startsWith("/hiring/api/jobs")` —
 * that entry exists for the PUBLIC careers listing. A view named
 * `/hiring/api/jobs/views` or `/hiring/api/jobs-board` would therefore have
 * silently published candidate names, emails, interview notes and scorecards to
 * unauthenticated callers, because the gate matches on prefix. Under
 * `/hiring/api/views/` these reads require a session once a credential is
 * registered, like every other internal read. **Do not rename these paths onto
 * the `jobs` prefix.**
 *
 * Reads are not capability-gated on the local plane (`excellent/server/authz.ts`
 * — "READS ARE NOT GATED", a cited decision: the local reader already holds the
 * disk and the SQLCipher key). So each mount carries the `READ` sentinel, and the
 * confidentiality boundary these ops actually rely on is tenant scoping — which
 * is why every handler resolves the tenant and passes `workspace_id` down.
 */

import { z } from "zod";
import type { Operation } from "../types";
import { jobSchema } from "./hiring-jobs";
import { jobStatus } from "./hiring-jobs";
import { pipelineSchema, stageKind, stageSchema } from "./hiring-pipelines";
import {
  applicationSchema,
  applicationWithJoinsSchema,
} from "./hiring-applications";
import { scorecardSchema, scorecardTemplateSchema } from "./hiring-scorecards";
import { jobSourceSchema } from "./hiring-sources";

/**
 * A job as a VIEW row: the published `jobSchema` plus the synced `uid`.
 *
 * `uid` is not an invention — `listJobs`/`getJobBySlug` are `SELECT *`, so the row
 * has always carried it and the Next views cast for it inline (`as JobRow & { uid:
 * string }`). Surfacing it on the contract is what lets a row open at the
 * scope-free `/o/<uid>`; the section-deep `${basePath}/jobs/<slug>` route 404s at
 * the workspace tier, which is the reason the cast existed.
 */
export const jobViewSchema = jobSchema.extend({ uid: z.string() });
export type JobViewShape = z.infer<typeof jobViewSchema>;

/** A note as the views render it — body + timestamp, nothing else is displayed. */
export const viewNoteSchema = z.object({
  id: z.number(),
  body: z.string(),
  created_at: z.string(),
});
export type ViewNoteShape = z.infer<typeof viewNoteSchema>;

export const viewIdentifierSchema = z.object({
  id: z.number(),
  kind: z.string(),
  value: z.string(),
  is_primary: z.number(),
});

export const viewAttachmentSchema = z.object({
  id: z.number(),
  filename: z.string(),
  mime_type: z.string().nullable(),
  byte_size: z.number().nullable(),
  created_at: z.string(),
});

export const viewSyncLinkSchema = z.object({
  source: z.string(),
  lastSyncedAt: z.string().nullable(),
});

// ─────────────────────────────────────────────────────── 1. jobs list

export const hiringJobsViewQuery = z.object({
  status: jobStatus.optional(),
  q: z.string().optional(),
});

export const hiringJobsViewResponse = z.object({
  jobs: z.array(
    jobViewSchema.extend({
      /** Applications across every stage — the row's one context chip. */
      applicant_count: z.number(),
    }),
  ),
});
export type HiringJobsViewPayload = z.infer<typeof hiringJobsViewResponse>;

export const hiringJobsView: Operation<
  void,
  z.infer<typeof hiringJobsViewQuery>,
  void,
  HiringJobsViewPayload
> = {
  method: "GET",
  path: () => "/hiring/api/views/jobs",
  query: hiringJobsViewQuery,
  response: hiringJobsViewResponse,
};

// ─────────────────────────────────────────────────────── 2. job detail

const slugParams = z.object({ slug: z.string().min(1) });

/**
 * Stage tallies as an ARRAY of pairs, not `Record<number, number>`.
 *
 * JSON object keys are strings, so a `Record<number, …>` round-trips as
 * `{"7": 3}` and every consumer has to remember to re-coerce. The pair list is
 * unambiguous on the wire and the client rebuilds the lookup once.
 */
export const stageCountSchema = z.object({
  stage_id: z.number(),
  count: z.number(),
});

export const hiringJobViewResponse = z.object({
  /** `null` is the ported `notFound()` — rendered by the view, never thrown. */
  job: jobViewSchema.nullable(),
  stages: z.array(stageSchema),
  stageCounts: z.array(stageCountSchema),
  total: z.number(),
});
export type HiringJobViewPayload = z.infer<typeof hiringJobViewResponse>;

export const hiringJobView: Operation<
  { slug: string },
  void,
  void,
  HiringJobViewPayload
> = {
  method: "GET",
  path: ({ slug }) => `/hiring/api/views/jobs/${encodeURIComponent(slug)}`,
  params: slugParams,
  response: hiringJobViewResponse,
};

// ─────────────────────────────────────────────────────── 3. job pipeline

export const hiringJobPipelineViewResponse = z.object({
  job: jobViewSchema.nullable(),
  activeStages: z.array(stageSchema),
  rejectedStage: stageSchema.nullable(),
  applications: z.array(applicationWithJoinsSchema),
});
export type HiringJobPipelineViewPayload = z.infer<
  typeof hiringJobPipelineViewResponse
>;

export const hiringJobPipelineView: Operation<
  { slug: string },
  void,
  void,
  HiringJobPipelineViewPayload
> = {
  method: "GET",
  path: ({ slug }) =>
    `/hiring/api/views/jobs/${encodeURIComponent(slug)}/pipeline`,
  params: slugParams,
  response: hiringJobPipelineViewResponse,
};

// ─────────────────────────────────────────────────────── 4. candidates list

export const hiringCandidatesViewQuery = z.object({
  q: z.string().optional(),
  stage: stageKind.optional(),
});

/**
 * Narrower than `hiring-candidates`' `candidateRowSchema` on purpose: that one is
 * the paginated RESOURCE row and lacks both `uid` and `latest_stage_kind`, which
 * the WorkRow list needs (the kind drives the state ring's phase, the name is the
 * label override). `listCandidates` already returns both — see
 * `packages/core/src/hiring/server/candidates.ts` `CandidateRow`.
 */
export const candidateViewRowSchema = z.object({
  person_id: z.number(),
  uid: z.string(),
  display_name: z.string(),
  primary_email: z.string().nullable(),
  latest_job_title: z.string().nullable(),
  latest_stage_name: z.string().nullable(),
  latest_stage_kind: stageKind.nullable(),
});
export type CandidateViewRowShape = z.infer<typeof candidateViewRowSchema>;

export const hiringCandidatesViewResponse = z.object({
  candidates: z.array(candidateViewRowSchema),
});
export type HiringCandidatesViewPayload = z.infer<
  typeof hiringCandidatesViewResponse
>;

export const hiringCandidatesView: Operation<
  void,
  z.infer<typeof hiringCandidatesViewQuery>,
  void,
  HiringCandidatesViewPayload
> = {
  method: "GET",
  path: () => "/hiring/api/views/candidates",
  query: hiringCandidatesViewQuery,
  response: hiringCandidatesViewResponse,
};

// ─────────────────────────────────────────────────────── 5. candidate detail

const idParams = z.object({ id: z.coerce.number().int() });

export const hiringCandidateViewResponse = z.object({
  person: z
    .object({
      id: z.number(),
      display_name: z.string(),
      created_at: z.string(),
    })
    .nullable(),
  identifiers: z.array(viewIdentifierSchema),
  attachments: z.array(viewAttachmentSchema),
  applications: z.array(
    applicationSchema.extend({
      uid: z.string(),
      /** Resolved server-side; the Next view called `getJobById` per row. */
      job_title: z.string().nullable(),
      /** Resolved server-side; the Next view called `getStageById` per row. */
      stage_name: z.string().nullable(),
    }),
  ),
  syncLink: viewSyncLinkSchema.nullable(),
  notes: z.array(viewNoteSchema),
  tags: z.array(
    z.object({
      id: z.number(),
      slug: z.string(),
      name: z.string(),
      color: z.string().nullable(),
    }),
  ),
});
export type HiringCandidateViewPayload = z.infer<
  typeof hiringCandidateViewResponse
>;

export const hiringCandidateView: Operation<
  { id: number },
  void,
  void,
  HiringCandidateViewPayload
> = {
  method: "GET",
  path: ({ id }) => `/hiring/api/views/candidates/${id}`,
  params: idParams,
  response: hiringCandidateViewResponse,
};

// ─────────────────────────────────────────────────────── 6. application detail

export const stageHistorySchema = z.object({
  id: z.number(),
  stage_id: z.number(),
  note: z.string().nullable(),
  entered_at: z.string(),
  exited_at: z.string().nullable(),
});

export const hiringApplicationViewResponse = z.object({
  application: applicationSchema.nullable(),
  job: jobSchema.nullable(),
  person: z.object({ id: z.number(), display_name: z.string() }).nullable(),
  identifiers: z.array(viewIdentifierSchema),
  attachments: z.array(viewAttachmentSchema),
  stages: z.array(stageSchema),
  rejectedStage: stageSchema.nullable(),
  currentStage: stageSchema.nullable(),
  history: z.array(stageHistorySchema),
  scorecards: z.array(scorecardSchema),
  templates: z.array(scorecardTemplateSchema),
  notes: z.array(viewNoteSchema),
  personNotes: z.array(viewNoteSchema),
});
export type HiringApplicationViewPayload = z.infer<
  typeof hiringApplicationViewResponse
>;

export const hiringApplicationView: Operation<
  { id: number },
  void,
  void,
  HiringApplicationViewPayload
> = {
  method: "GET",
  path: ({ id }) => `/hiring/api/views/applications/${id}`,
  params: idParams,
  response: hiringApplicationViewResponse,
};

// ─────────────────────────────────────────────────────── 7. pipeline hub

export const hiringPipelineHubViewResponse = z.object({
  jobs: z.array(
    z.object({
      id: z.number(),
      slug: z.string(),
      title: z.string(),
      department: z.string().nullable(),
      location: z.string().nullable(),
      total: z.number(),
    }),
  ),
});
export type HiringPipelineHubViewPayload = z.infer<
  typeof hiringPipelineHubViewResponse
>;

export const hiringPipelineHubView: Operation<
  void,
  void,
  void,
  HiringPipelineHubViewPayload
> = {
  method: "GET",
  path: () => "/hiring/api/views/pipeline-hub",
  response: hiringPipelineHubViewResponse,
};

// ─────────────────────────────────────────────────────── 8. settings

export const hiringSettingsViewResponse = z.object({
  pipelines: z.array(pipelineSchema),
  templates: z.array(scorecardTemplateSchema),
  sources: z.array(jobSourceSchema),
  providers: z.array(
    z.object({
      slug: z.string(),
      label: z.string(),
      status: z.string(),
      authMode: z.string().optional(),
      connectionCount: z.number(),
    }),
  ),
  /**
   * The ATS detected from the user's careers page (`enrichedAtsChoice()`),
   * already resolved against `providers` — `null` when nothing was detected or
   * the detected slug is not one this product consumes.
   */
  detectedProvider: z
    .object({ slug: z.string(), label: z.string(), connectionCount: z.number() })
    .nullable(),
  /**
   * `HIRING_DISPLAY_NAME`, resolved SERVER-side.
   *
   * This one field is why the settings view needed an endpoint at all beyond its
   * four loaders. The Next original did `import { HIRING_DISPLAY_NAME } from
   * "@excellent/core"` for a single string constant — and `client/vite.config.ts`'s
   * own header records that exact import, from `packages/ui/src/path-label.ts`,
   * dragging `security/{keystore,keychain}` and `better-sqlite3` into the client
   * graph eight hops down with 30 externalization warnings. The barrel is not
   * browser-safe (`index → objectives → events/engine → db → security/keystore`),
   * so the label crosses the wire instead of the import.
   */
  productLabel: z.string(),
});
export type HiringSettingsViewPayload = z.infer<
  typeof hiringSettingsViewResponse
>;

export const hiringSettingsView: Operation<
  void,
  void,
  void,
  HiringSettingsViewPayload
> = {
  method: "GET",
  path: () => "/hiring/api/views/settings",
  response: hiringSettingsViewResponse,
};

// ─────────────────────────────────────────────────────── barrel

export const hiringViews = {
  jobs: hiringJobsView,
  job: hiringJobView,
  jobPipeline: hiringJobPipelineView,
  candidates: hiringCandidatesView,
  candidate: hiringCandidateView,
  application: hiringApplicationView,
  pipelineHub: hiringPipelineHubView,
  settings: hiringSettingsView,
};
